import { keyframes, styled } from "goober";
import {
  memo,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { playClick } from "../../../lib/sounds";
import { Icon } from "../../Icon";
import { Text } from "../../Text";
import type { GraphNode } from "./buildAccessModel";
import { NODE_HEIGHT, NODE_WIDTH, type NodePosition } from "./layoutGraph";

export type ChipState =
  | "normal"
  | "active"
  | "dim"
  | "hidden"
  | "dragging"
  | "droppable"
  | "dropTarget";

const Chip = styled("button")`
  position: absolute;
  z-index: 3;
  scroll-margin: 56px 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  width: ${NODE_WIDTH}px;
  height: ${NODE_HEIGHT}px;
  padding: 8px;
  box-sizing: border-box;
  border: none;
  /* Hover and selection are shown by the inset ring, so the focus ring is just
     noise here. */
  outline: none;
  background: var(--surface-primary);
  border-radius: 12px !important;
  box-shadow:
    0 0 0 3px var(--surface-secondary),
    inset 0 0 0 1px var(--gray-200);
  cursor: pointer;
  text-align: left;
  user-select: none;
  touch-action: none;
  transition:
    background 120ms ease,
    filter 160ms ease,
    opacity 160ms ease,
    transform 160ms ease;

  &:hover {
    box-shadow:
      0 0 0 3px var(--surface-secondary),
      inset 0 0 0 1px var(--gray-600);
  }
  &[data-state="active"] {
    box-shadow:
      0 0 0 3px var(--surface-secondary),
      inset 0 0 0 1px var(--gray-1000);
  }
  /* Sit above the non-highlighted edge layer (z 0) but below the highlighted
     edges (z 2), so dimmed lines hide behind dimmed cards and only highlighted
     lines travel over them. */
  &[data-state="dim"] {
    z-index: 1;
    filter: blur(3px) grayscale(1);
  }
  &[data-state="dim"] > * {
    opacity: 0.5;
  }
  /* Hide mode: the dimmed cards fade and shrink away entirely instead of
     blurring, so a focused path reads cleanly in a dense map. */
  &[data-state="hidden"] {
    opacity: 0;
    transform: scale(0.96);
    pointer-events: none;
  }
  /* The picked-up card floats with the cursor; leave a blank slot behind. */
  &[data-state="dragging"] {
    background: var(--alpha4);
  }
  &[data-state="dragging"] > * {
    opacity: 0;
  }
  &[data-state="dropTarget"] {
    background:
      linear-gradient(var(--alpha8), var(--alpha8)), var(--surface-primary);
  }
  &[data-state="dropTarget"] rect {
    stroke: var(--gray-600);
  }
  &:hover [data-more] {
    opacity: 1;
  }
  /* Make room for the more-button so a long name/email truncates before it
     rather than running underneath. */
  &:hover:has([data-more]) [data-body] {
    padding-right: 32px;
  }
  &[data-state="dim"] [data-more] {
    display: none;
  }
`;

const Body = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
  transition: padding-right 120ms ease;
`;

const march = keyframes`
  to {
    stroke-dashoffset: -18;
  }
`;

// Marching-ants border marking a card that can accept the dragged one.
const DashedBorder = styled("svg")`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;

  rect {
    fill: none;
    stroke: var(--gray-400);
    stroke-width: 1;
    stroke-dasharray: 5 4;
    animation: ${march} 0.6s linear infinite;
  }
`;

// Hover-revealed actions opener, pinned to the card's right edge. Its background
// matches the hovered card so it masks any content (e.g. the admin crown or a
// long name) behind it.
const MoreButton = styled("div")`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: var(--text-secondary);
  background: var(--surface-primary);
  opacity: 0;
  cursor: pointer;
  transition:
    opacity 120ms ease,
    background 120ms ease,
    color 120ms ease;

  &:hover {
    background: var(--gray-200);
    color: var(--text-primary);
  }
`;

type Props = {
  node: GraphNode;
  position: NodePosition;
  leftVisual: ReactNode;
  state: ChipState;
  onPointerDown: (node: GraphNode, e: ReactPointerEvent) => void;
  onMore?: (node: GraphNode) => void;
};

export const GraphNodeChip = memo(function GraphNodeChip({
  node,
  position,
  leftVisual,
  state,
  onPointerDown,
  onMore,
}: Props) {
  const showDashed = state === "droppable" || state === "dropTarget";
  return (
    <Chip
      data-no-pill
      data-node-id={node.id}
      data-state={state}
      style={{ left: position.x, top: position.y }}
      onPointerDown={(e) => onPointerDown(node, e)}
    >
      {leftVisual}
      <Body data-body>
        <Text variant="text-small-md" ellipsis color="var(--text-primary)">
          {node.name}
        </Text>
        {node.subtitle && (
          <Text
            variant="text-smaller-md"
            ellipsis
            color="var(--text-secondary)"
          >
            {node.subtitle}
          </Text>
        )}
      </Body>
      {onMore && (
        <MoreButton
          data-more
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            playClick();
            onMore(node);
          }}
        >
          <Icon name="more" size={16} color="currentColor" />
        </MoreButton>
      )}
      {showDashed && (
        <DashedBorder>
          <rect
            x="0.5"
            y="0.5"
            width="calc(100% - 1px)"
            height="calc(100% - 1px)"
            rx="11.5"
          />
        </DashedBorder>
      )}
    </Chip>
  );
});
